import {
  useCallback,
  useEffect,
  useMemo,
  FC,
  memo,
  useState,
  startTransition,
} from 'react';
import {
  Box,
  Checkbox,
  FormControlLabel,
  Paper,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useGetAccountTagsQuery } from '@/store/accountsApi';

export interface TagsTabProps {
  initialExcludedTags?: number[];
  setFieldValue: (field: string, value: number[]) => void;
}

interface FlatTag {
  InvestorTagId: number;
  Name: string;
}

const TagsTab: FC<TagsTabProps> = memo(
  ({ initialExcludedTags = [], setFieldValue }) => {
    const { t } = useTranslation();
    const [excludedTags, setExcludedTags] =
      useState<number[]>(initialExcludedTags);

    useEffect(() => {
      setExcludedTags(initialExcludedTags);
    }, [initialExcludedTags]);

    const { data: accountGroups, isLoading } = useGetAccountTagsQuery();

    const flatTags = useMemo<FlatTag[]>(() => {
      if (!accountGroups?.length) return [];
      const seen = new Set<number>();
      const list: FlatTag[] = [];
      // API can return null Name despite the string type — cast to acknowledge runtime reality
      type RuntimeTag = { Name: string | null; InvestorTagId: number };
      for (const group of accountGroups) {
        const groupTags = group.Tags as unknown as RuntimeTag[];
        for (const tag of groupTags) {
          if (tag.Name != null && !seen.has(tag.InvestorTagId)) {
            seen.add(tag.InvestorTagId);
            list.push({ InvestorTagId: tag.InvestorTagId, Name: tag.Name });
          }
        }
      }
      return list.sort((a, b) => a.Name.localeCompare(b.Name));
    }, [accountGroups]);

    const handleTagChange = useCallback(
      (investorTagId: number) => {
        setExcludedTags((prev) => {
          const isExcluded = prev.includes(investorTagId);
          const next = isExcluded
            ? prev.filter((id) => id !== investorTagId)
            : [...prev, investorTagId];
          startTransition(() => {
            setFieldValue('excludedTags', next);
          });
          return next;
        });
      },
      [setFieldValue],
    );

    if (isLoading) {
      return (
        <Paper sx={{ p: 2 }}>
          <Typography color='text.secondary'>{t('loading')}</Typography>
        </Paper>
      );
    }

    if (flatTags.length === 0) {
      return (
        <Paper sx={{ p: 2, maxHeight: '400px', overflow: 'auto' }}>
          <Typography color='text.secondary'>
            {t('filters.noTagsAvailable')}
          </Typography>
        </Paper>
      );
    }

    return (
      <Box
        sx={{
          p: 2,
          display: 'flex',
          flexDirection: 'column',
          flex: 1,
          minHeight: 0,
        }}
      >
        <Paper sx={{ p: 2, maxHeight: '400px', overflow: 'auto', flex: 1 }}>
          <Typography
            variant='subtitle2'
            color='text.secondary'
            sx={{ mb: 2 }}
          >
            {t('filters.excludeTagsDescription')}
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            {flatTags.map((tag) => (
              <FormControlLabel
                key={`${tag.Name}-${String(tag.InvestorTagId)}`}
                control={
                  <Checkbox
                    checked={excludedTags.includes(tag.InvestorTagId)}
                    onChange={() => {
                      handleTagChange(tag.InvestorTagId);
                    }}
                    size='small'
                  />
                }
                label={tag.Name}
              />
            ))}
          </Box>
        </Paper>
      </Box>
    );
  },
);

TagsTab.displayName = 'TagsTab';

export default TagsTab;
